import {computed, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformServer} from "@angular/common";
import {combineLatest, firstValueFrom, map, Observable, of, ReplaySubject, switchMap, tap} from "rxjs";
import {ApartmentDto} from "homey-housing-lottery-app-dto";
import {getLowerIncomeBound, getUpperIncomeBound, IncomeOption} from "../model/user";
import {
  filterRelevantApartmentsForIncomeAndSize,
  isUserEligibleForApartment,
  Lottery,
  sortApartmentsByBestFit
} from "../model/lottery";
import {UserService} from "./user.service";
import {toSignal} from "@angular/core/rxjs-interop";
import {HttpClient} from '@angular/common/http';
import {environment} from "../../../environments/environment";


@Injectable({
  providedIn: 'root'
})
export class ApartmentService {
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  constructor(private readonly userService: UserService, private readonly http: HttpClient) {
  }

  public readonly apartments$ = new ReplaySubject<ApartmentDto[]>(1);
  public readonly apartmentsSortedByPreferred$ = combineLatest(
    [this.apartments$, this.userService.user$]
  ).pipe(
    map(([apartments, user]) => sortApartmentsByBestFit(apartments, user)),
  )

  public async retrieveData() {
    await firstValueFrom(this.retrieveApartments());
  }

  private retrieveApartments(): Observable<ApartmentDto[]> {
    return this.getApartmentsViaHttp()
      .pipe(
        tap(apartments => {
          this.apartments$.next(apartments)
        })
      )
  }

  public getApartmentsViaHttp() {
    return this.http.get<ApartmentDto[]>(`${environment.apiUrl}/api/public/apartments`)
  }

  public readonly apartments = toSignal(this.apartments$);
  public readonly openLotteries = computed(
    () => this.apartments()?.filter(lottery => lottery.status === 'open')
  )

  public readonly openLotteries$: Observable<ApartmentDto[]> = this.apartments$.pipe(
    map(lotteries => lotteries.filter(lottery => lottery.status === 'open'))
  )

  public readonly relevantApartments = computed(() => {
    const user = this.userService.user();
    if (!user) {
      return [];
    }

    const apartments = this.openLotteries()?.filter(apartment => isUserEligibleForApartment(apartment, user)) ?? [];
    return sortApartmentsByBestFit(apartments, user);
  });

  public getRelevantApartments(): Observable<Lottery[]> {
    return combineLatest([
      this.openLotteries$,
      this.userService.user$
    ]).pipe(
      map(([apartments, user]) =>
        sortApartmentsByBestFit(apartments.filter(apartment => isUserEligibleForApartment(apartment, user)), user)),
    );
  }

  public getRelevantApartmentsForIncomeAndSize(income: IncomeOption, size: number): Observable<Lottery[]> {
    return this.openLotteries$.pipe(
      map(apartments => filterRelevantApartmentsForIncomeAndSize(
        apartments,
        {
        lowerHouseholdIncome: getLowerIncomeBound(income),
        upperHouseholdIncome: getUpperIncomeBound(income),
        size,
        receivesRentalAssistance: false,
        eligibleForSenior: true
        })),
    );
  }

  public getApartmentOrUndefined(apartmentIdOrSlug: string): Observable<Lottery | undefined> {
    let retryStarted = false;
    return this.apartments$.pipe(
      map(apartments => this.findApartment(apartments, apartmentIdOrSlug)),
      switchMap(apartment => {
          if (apartment) {
            return of(apartment);
          } else if (!retryStarted && !this.isServer) {
            retryStarted = true;
            return this.retrieveApartments().pipe(
              map(apartments => this.findApartment(apartments, apartmentIdOrSlug)),
            );
          } else {
            return of(undefined);
          }
        }
      ),
    );
  }

  public getApartment(apartmentIdOrSlug: string): Observable<Lottery> {
    return this.getApartmentOrUndefined(apartmentIdOrSlug).pipe(
      map(apartment => {
        if (apartment === undefined) {
          throw new Error(`Apartment with id ${apartmentIdOrSlug} not found`);
        }
        return apartment;
      })
    );
  }

  private findApartment(apartments: ApartmentDto[], apartmentIdOrSlug: string) {
    return apartments.find(apartment => apartment.id === apartmentIdOrSlug || apartment.slug === apartmentIdOrSlug)
  }
}
